import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
  useColorModeValue,
  Skeleton,
  Text,
  useToast,
  Badge,
  VStack,
  HStack,
  SimpleGrid,
  Tag,
  TagLabel,
  TagLeftIcon,
  Tooltip,
  Card,
  CardBody,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { motion } from 'framer-motion';
import { SearchIcon } from '@chakra-ui/icons';
import { AiOutlineDeploymentUnit } from 'react-icons/ai';
import { GiModernCity } from 'react-icons/gi';
import { FaBuilding } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import baseUrl from 'Base_Url/baseUrl';
import axiosInstance from 'axiosInstance';

const floatAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-3px); }
  100% { transform: translateY(0px); }
`;

const shimmerAnimation = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

const MotionBox = motion(Box);
const MotionTag = motion(Tag);

const SubDivision = () => {
  const { t } = useTranslation();
  const toast = useToast();

  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [circles, setCircles] = useState([]);

  const textColor = useColorModeValue('gray.800', 'white');
  const subtitleColor = useColorModeValue('gray.600', 'gray.300');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const gradientStart = useColorModeValue('blue.400', 'blue.600');
  const gradientEnd = useColorModeValue('cyan.500', 'cyan.600');
  const cardBg = useColorModeValue('white', 'gray.800');

  const fetchSubDivisions = useCallback(
    async (search = '') => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await axiosInstance.get(
          `${baseUrl}/master/all-subdivisionsmains?search=${encodeURIComponent(
            search,
          )}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (res.status === 200) {
          const responseData = res.data?.data;

          let subdivisionsData = [];

          if (responseData?.data?.data) {
            subdivisionsData = responseData.data.data;
          } else if (responseData?.data) {
            subdivisionsData = responseData.data;
          } else if (Array.isArray(responseData)) {
            subdivisionsData = responseData;
          }

          setCircles(Array.isArray(subdivisionsData) ? subdivisionsData : []);
        } else {
          setCircles([]);
        }
      } catch (err) {
        if (err.response && err.response.status !== 401) {
          toast({
            title: 'Error fetching Sub-Divisions',
            description:
              err.response?.data?.message ||
              err.response?.data?.data ||
              'Please try again.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
        setCircles([]);
      } finally {
        setLoading(false);
      }
    },
    [toast],
  );

  useEffect(() => {
    fetchSubDivisions('');
  }, [fetchSubDivisions]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchSubDivisions(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchInput, fetchSubDivisions]);

  const filteredCircles = circles.filter((s) =>
    s.name?.toLowerCase().includes(searchInput.toLowerCase()),
  );

  const stats = {
    total: circles.length,
    withDistrict: circles.filter((c) => c.district?.name).length,
    withDivision: circles.filter((c) => c.division?.name).length,
  };

  return (
    <Box>
      <Card
        mb={4}
        borderRadius="lg"
        bg={cardBg}
        border="1px solid"
        borderColor={borderColor}
        overflow="hidden"
      >
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          height="6px"
          bgGradient={`linear(to-r, ${gradientStart}, ${gradientEnd})`}
        />

        <CardBody p={4}>
          <Flex align="center" gap={4} mb={6}>
            <Box
              p={3}
              borderRadius="xl"
              bg="blue.100"
              _dark={{ bg: 'blue.900' }}
              position="relative"
              overflow="hidden"
            >
              <Icon as={AiOutlineDeploymentUnit} boxSize={6} color="blue.500" />
            </Box>
            <VStack align="flex-start" spacing={1}>
              <Heading size="md" color={textColor}>
                Sub-Divisions
              </Heading>
              <Text fontSize="sm" color={subtitleColor}>
                {stats.total} administrative sub-divisions
              </Text>
            </VStack>
          </Flex>

          {/* <Flex mb={6} gap={4} wrap="wrap" align="center">
        <InputGroup size="md" flex="1" minW="300px">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="blue.500" />
          </InputLeftElement>
          <Input
            placeholder="Search sub-divisions..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            color={textColor}
            borderRadius="lg"
            border="2px solid"
            borderColor={borderColor}
            _focus={{
              borderColor: 'blue.500',
              boxShadow: '0 0 0 1px rgba(66, 153, 225, 0.6)',
            }}
            _hover={{
              borderColor: 'blue.300',
            }}
          />
        </InputGroup>
        
        <Badge colorScheme="blue" variant="subtle" px={4} py={2} borderRadius="full">
          {filteredCircles.length} items
        </Badge>
      </Flex> */}

          <Box>
            {loading ? (
              <SimpleGrid
                columns={{ base: 2, sm: 3, md: 4, lg: 5 }}
                spacing={3}
              >
                {Array.from({ length: 10 }).map((_, i) => (
                  <Skeleton key={i} height="40px" borderRadius="lg" />
                ))}
              </SimpleGrid>
            ) : filteredCircles.length === 0 ? (
              <VStack
                spacing={4}
                p={8}
                textAlign="center"
                borderRadius="lg"
                border="2px dashed"
                borderColor={borderColor}
              >
                <Icon
                  as={AiOutlineDeploymentUnit}
                  boxSize={12}
                  color="gray.400"
                />
                <Text color={subtitleColor} fontSize="lg" fontWeight="medium">
                  {circles.length === 0
                    ? 'No sub-divisions found'
                    : 'No matching sub-divisions found'}
                </Text>
                <Text color={subtitleColor} fontSize="sm">
                  {circles.length === 0
                    ? 'Start by adding some sub-divisions'
                    : 'Try a different search term'}
                </Text>
              </VStack>
            ) : (
              <SimpleGrid
                columns={{ base: 2, sm: 3, md: 4, lg: 5 }}
                spacing={3}
              >
                {filteredCircles.map((sub, index) => (
                  <Tooltip
                    key={sub._id || index}
                    label={
                      <Box>
                        <Text fontWeight="bold">{sub.name?.toUpperCase()}</Text>
                        {sub.district?.name && (
                          <Text fontSize="sm">
                            District: {sub.district.name}
                          </Text>
                        )}
                        {sub.division?.name && (
                          <Text fontSize="sm">
                            Division: {sub.division.name}
                          </Text>
                        )}
                        <Text fontSize="sm">
                          Status: {sub.status || 'Active'}
                        </Text>
                      </Box>
                    }
                    hasArrow
                    placement="top"
                    bg="white"
                    color="gray.800"
                    _dark={{ bg: 'gray.700', color: 'white' }}
                    borderRadius="lg"
                    p={3}
                  >
                    <MotionTag
                      size="lg"
                      variant="subtle"
                      colorScheme={
                        sub.status === 'active'
                          ? 'blue'
                          : sub.district?.name
                          ? 'green'
                          : sub.division?.name
                          ? 'purple'
                          : 'gray'
                      }
                      borderRadius="full"
                      px={4}
                      py={3}
                      cursor="pointer"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <TagLeftIcon as={AiOutlineDeploymentUnit} />
                      <TagLabel
                        fontSize="sm"
                        fontWeight="medium"
                        maxW="120px"
                        isTruncated
                      >
                        {sub.name?.toUpperCase() || 'N/A'}
                      </TagLabel>
                    </MotionTag>
                  </Tooltip>
                ))}
              </SimpleGrid>
            )}
          </Box>

          <Flex
            justify="space-between"
            align="center"
            mt={6}
            pt={4}
            borderTop="1px solid"
            borderColor={borderColor}
          >
            <Text fontSize="sm" color={subtitleColor}>
              Showing <strong>{filteredCircles.length}</strong> of{' '}
              <strong>{circles.length}</strong> sub-divisions
            </Text>
            <Text fontSize="xs" color={subtitleColor}>
              Updated:{' '}
              {new Date().toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </Text>
          </Flex>
        </CardBody>
      </Card>
    </Box>
  );
};

export default SubDivision;
